<template>
  <div>
    <div class="page-header">
      <div>
        <h5 class="page-title">Visitor Documents</h5>
        <p class="page-sub">Manage NDAs, policies and induction materials — each document is versioned so you know exactly what was signed</p>
      </div>
      <button class="btn-primary-action" @click="openCreate">
        <font-awesome-icon icon="plus" /> New Document
      </button>
    </div>

    <div v-if="loading" class="sk-table">
      <div v-for="n in 3" :key="n" class="sk-row" style="height:80px;"></div>
    </div>

    <div v-else-if="!docs.length" class="empty-state">
      <font-awesome-icon icon="file-signature" class="empty-icon" />
      <p class="empty-title">No documents yet</p>
      <p class="empty-sub">Create versioned documents (NDAs, policies) that visitors sign during check-in. Each version is stored permanently.</p>
      <button class="btn-primary-action" @click="openCreate">Create First Document</button>
    </div>

    <div v-else class="docs-list">
      <div v-for="doc in docs" :key="doc.id" class="doc-card">
        <div class="doc-header">
          <div class="doc-info">
            <div class="doc-title-row">
              <span class="doc-icon-wrap" :class="`doc-icon--${doc.type}`"><font-awesome-icon :icon="typeIcon(doc.type)" /></span>
              <span class="doc-name">{{ doc.name }}</span>
              <span class="badge" :class="`type-badge--${doc.type}`">{{ typeName(doc.type) }}</span>
              <span class="badge" :class="doc.active ? 'badge--green' : 'badge--grey'">{{ doc.active ? 'Active' : 'Inactive' }}</span>
            </div>
            <div class="doc-version-info" v-if="doc.current_version">
              Version {{ doc.current_version.version_number }} · Effective {{ formatDate(doc.current_version.effective_from) }}
            </div>
            <div v-else class="doc-version-info doc-no-version">No versions yet — add a version to activate this document.</div>
          </div>
          <div class="doc-actions">
            <button class="btn-ghost-sm" @click="openVersions(doc)"><font-awesome-icon icon="history" /> Versions</button>
            <button class="btn-ghost-sm" @click="openAddVersion(doc)"><font-awesome-icon icon="plus" /> New Version</button>
            <button class="btn-icon" @click="openEdit(doc)"><font-awesome-icon icon="pencil-alt" /></button>
          </div>
        </div>
      </div>
    </div>

    <!-- Create/Edit Document Modal -->
    <div v-if="docModal" class="modal-backdrop" @mousedown.self="docModal = false">
      <div class="modal-box modal-box--sm">
        <div class="modal-header">
          <h6 class="modal-title">{{ editingDoc ? 'Edit Document' : 'New Document' }}</h6>
          <button class="modal-close" @click="docModal = false"><font-awesome-icon icon="times" /></button>
        </div>
        <div class="modal-body">
          <div class="form-row">
            <label class="form-label">Document Name <span class="req">*</span></label>
            <input v-model="docForm.name" class="form-input" maxlength="100" placeholder="e.g. Non-Disclosure Agreement" />
          </div>
          <div class="form-row" style="margin-top:14px;">
            <label class="form-label">Type</label>
            <select v-model="docForm.type" class="form-input">
              <option value="nda">NDA</option>
              <option value="induction">Induction</option>
              <option value="policy">Policy</option>
              <option value="other">Other</option>
            </select>
          </div>
          <div class="toggle-row" style="margin-top:14px;">
            <span class="toggle-name">Active</span>
            <label class="toggle-switch"><input type="checkbox" v-model="docForm.active" /><span class="toggle-track"></span></label>
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn-ghost" @click="docModal = false">Cancel</button>
          <button class="btn-primary-action" @click="saveDoc" :disabled="savingDoc">
            <font-awesome-icon v-if="savingDoc" icon="spinner" spin />
            {{ savingDoc ? 'Saving…' : (editingDoc ? 'Save' : 'Create') }}
          </button>
        </div>
      </div>
    </div>

    <!-- Add Version Modal -->
    <div v-if="versionModal" class="modal-backdrop" @mousedown.self="versionModal = false">
      <div class="modal-box" style="max-width:640px;">
        <div class="modal-header">
          <h6 class="modal-title">New Version — {{ versionDoc?.name }}</h6>
          <button class="modal-close" @click="versionModal = false"><font-awesome-icon icon="times" /></button>
        </div>
        <div class="modal-body">
          <div class="form-row">
            <label class="form-label">Effective From <span class="req">*</span></label>
            <input v-model="versionForm.effective_from" type="date" class="form-input" style="max-width:200px;" />
          </div>
          <div class="form-row" style="margin-top:14px;">
            <label class="form-label">Document Content <span class="req">*</span></label>
            <textarea v-model="versionForm.content" class="form-input" rows="12" style="resize:vertical;line-height:1.6;font-family:monospace;font-size:12px;" placeholder="Paste the full document text here. This exact version will be shown to visitors and stored with their signature."></textarea>
          </div>
          <!-- Quiz (induction docs only) -->
          <div v-if="versionDoc && versionDoc.type === 'induction'" style="margin-top:18px;">
            <div class="quiz-editor-header">
              <div>
                <span class="form-label">Induction Quiz</span>
                <span class="form-hint" style="margin-top:2px;">Add questions visitors must answer after reading this document.</span>
              </div>
              <div style="display:flex;align-items:center;gap:12px;">
                <div class="form-row" style="flex-direction:row;align-items:center;gap:8px;margin:0;">
                  <label class="form-label" style="white-space:nowrap;">Pass mark %</label>
                  <input v-model.number="versionForm.quiz_pass_mark" type="number" min="1" max="100" class="form-input" style="width:72px;" />
                </div>
                <button class="btn-ghost" style="white-space:nowrap;" @click="addQuestion">+ Add Question</button>
              </div>
            </div>

            <div v-if="!versionForm.quiz_questions.length" class="quiz-empty">
              No questions yet. Add at least one question to enable the quiz.
            </div>

            <div v-for="(q, qi) in versionForm.quiz_questions" :key="qi" class="quiz-q-card">
              <div class="quiz-q-header">
                <span class="quiz-q-num">Q{{ qi + 1 }}</span>
                <input v-model="q.question" class="form-input" placeholder="Question text…" style="flex:1;" />
                <button class="btn-icon" style="color:#dc2626;" @click="removeQuestion(qi)" title="Remove question">
                  <font-awesome-icon icon="trash" />
                </button>
              </div>
              <div class="quiz-opts">
                <div v-for="(opt, oi) in q.options" :key="oi" class="quiz-opt-row">
                  <label class="quiz-opt-radio" :title="oi === q.correct_index ? 'Correct answer' : 'Mark as correct'">
                    <input type="radio" :name="'correct_' + qi" :value="oi" v-model="q.correct_index" />
                  </label>
                  <input v-model="q.options[oi]" class="form-input" :placeholder="'Option ' + (oi + 1)" style="flex:1;" />
                  <button v-if="q.options.length > 2" class="btn-icon" style="color:#aab8cc;" @click="removeOption(q, oi)" title="Remove option">
                    <font-awesome-icon icon="times" />
                  </button>
                </div>
                <button v-if="q.options.length < 5" class="btn-ghost" style="margin-top:4px;font-size:12px;" @click="q.options.push('')">+ Add Option</button>
              </div>
            </div>
          </div>

          <div class="version-warning">
            <font-awesome-icon icon="exclamation-circle" />
            Once saved, this version cannot be edited. Create a new version to make changes.
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn-ghost" @click="versionModal = false">Cancel</button>
          <button class="btn-primary-action" @click="saveVersion" :disabled="savingVersion">
            <font-awesome-icon v-if="savingVersion" icon="spinner" spin />
            {{ savingVersion ? 'Saving…' : 'Publish Version' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Version History Modal -->
    <div v-if="versionsModal" class="modal-backdrop" @mousedown.self="versionsModal = false">
      <div class="modal-box" style="max-width:680px;">
        <div class="modal-header">
          <h6 class="modal-title">Version History — {{ versionsDoc?.name }}</h6>
          <button class="modal-close" @click="versionsModal = false"><font-awesome-icon icon="times" /></button>
        </div>
        <div class="modal-body">
          <div v-if="loadingVersions" class="sk-table">
            <div v-for="n in 3" :key="n" class="sk-row"></div>
          </div>
          <div v-else-if="!versions.length" style="text-align:center;padding:40px 20px;color:#7a90b0;font-size:13px;">No versions yet.</div>
          <div v-else class="version-list">
            <div v-for="v in versions" :key="v.id" class="version-item">
              <div class="version-meta">
                <span class="version-num">v{{ v.version_number }}</span>
                <span class="version-effective">Effective: {{ formatDate(v.effective_from) }}</span>
                <span class="version-created">Added: {{ formatDate(v.created_at) }}</span>
                <span v-if="versionsDoc?.current_version_id === v.id" class="badge badge--green">Current</span>
              </div>
              <div class="version-preview">{{ v.content.slice(0, 240) }}{{ v.content.length > 240 ? '…' : '' }}</div>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn-ghost" @click="versionsModal = false">Close</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import { useToast } from 'vue-toastification'

export default {
  name: 'VisitorDocuments',
  setup() { return { toast: useToast() } },
  data() {
    return {
      loading: true, docs: [],
      docModal: false, editingDoc: null, savingDoc: false,
      docForm: { name: '', type: 'nda', active: true },
      versionModal: false, versionDoc: null, savingVersion: false,
      versionForm: { content: '', effective_from: new Date().toISOString().slice(0, 10), quiz_questions: [], quiz_pass_mark: 70 },
      versionsModal: false, versionsDoc: null, versions: [], loadingVersions: false,
    }
  },
  created() { this.load() },
  methods: {
    async load() {
      this.loading = true
      try {
        const r = await axios.get('visitors/documents')
        this.docs = r.data.data ?? r.data
      } catch { this.toast.error('Failed to load documents.') }
      finally { this.loading = false }
    },
    openCreate() { this.docForm = { name: '', type: 'nda', active: true }; this.editingDoc = null; this.docModal = true },
    openEdit(doc) { this.editingDoc = doc; this.docForm = { name: doc.name, type: doc.type, active: doc.active }; this.docModal = true },
    async saveDoc() {
      if (!this.docForm.name.trim()) { this.toast.error('Document name is required.'); return }
      this.savingDoc = true
      try {
        if (this.editingDoc) {
          await axios.put(`visitors/documents/${this.editingDoc.id}`, this.docForm)
          this.toast.success('Document updated.')
        } else {
          await axios.post('visitors/documents', this.docForm)
          this.toast.success('Document created.')
        }
        this.docModal = false; await this.load()
      } catch (e) { this.toast.error(e.response?.data?.message ?? 'Failed to save.') }
      finally { this.savingDoc = false }
    },
    openAddVersion(doc) { this.versionDoc = doc; this.versionForm = { content: '', effective_from: new Date().toISOString().slice(0, 10), quiz_questions: [], quiz_pass_mark: 70 }; this.versionModal = true },
    addQuestion() {
      this.versionForm.quiz_questions.push({ question: '', options: ['', ''], correct_index: 0 })
    },
    removeQuestion(qi) { this.versionForm.quiz_questions.splice(qi, 1) },
    removeOption(q, oi) {
      q.options.splice(oi, 1)
      if (q.correct_index >= q.options.length) q.correct_index = 0
    },
    async saveVersion() {
      if (!this.versionForm.content.trim()) { this.toast.error('Content is required.'); return }
      if (!this.versionForm.effective_from) { this.toast.error('Effective date is required.'); return }
      this.savingVersion = true
      try {
        await axios.post(`visitors/documents/${this.versionDoc.id}/versions`, this.versionForm)
        this.toast.success('Version published.')
        this.versionModal = false; await this.load()
      } catch (e) { this.toast.error(e.response?.data?.message ?? 'Failed to publish.') }
      finally { this.savingVersion = false }
    },
    async openVersions(doc) {
      this.versionsDoc = doc; this.versionsModal = true; this.versions = []; this.loadingVersions = true
      try {
        const r = await axios.get(`visitors/documents/${doc.id}/versions`)
        this.versions = r.data.data ?? r.data
      } catch { this.toast.error('Failed to load versions.') }
      finally { this.loadingVersions = false }
    },
    typeIcon(t) { return { nda: 'file-signature', induction: 'play-circle', policy: 'shield-alt', other: 'file-alt' }[t] ?? 'file-alt' },
    typeName(t) { return { nda: 'NDA', induction: 'Induction', policy: 'Policy', other: 'Other' }[t] ?? t },
    formatDate(d) { return d ? new Date(d).toLocaleDateString('en-GB', { day:'2-digit', month:'short', year:'numeric' }) : '—' },
  },
}
</script>

<style scoped>
.page-header { display:flex; align-items:flex-start; justify-content:space-between; flex-wrap:wrap; gap:12px; margin-bottom:20px; }
.page-title  { font-size:20px; font-weight:700; color:#1a2b45; margin:0 0 3px; }
.page-sub    { font-size:13px; color:#7a90b0; margin:0; }
.btn-primary-action { display:inline-flex; align-items:center; gap:6px; background:#004AAD; color:#fff; font-size:13px; font-weight:600; padding:0 18px; height:38px; border-radius:8px; border:none; cursor:pointer; font-family:inherit; }
.btn-primary-action:hover:not(:disabled) { background:#003b8e; }
.btn-primary-action:disabled { opacity:.6; cursor:not-allowed; }
.btn-ghost   { display:inline-flex; align-items:center; gap:6px; height:38px; padding:0 16px; border:1px solid #dde5f0; border-radius:8px; background:#fff; font-size:13px; color:#3d5068; cursor:pointer; font-family:inherit; }
.btn-ghost:hover { background:#f4f7fb; }
.btn-ghost-sm { display:inline-flex; align-items:center; gap:5px; height:32px; padding:0 12px; border:1px solid #dde5f0; border-radius:8px; background:#fff; font-size:12px; color:#3d5068; cursor:pointer; font-family:inherit; }
.btn-ghost-sm:hover { background:#f4f7fb; }
.btn-icon    { display:inline-flex; align-items:center; justify-content:center; width:32px; height:32px; border-radius:8px; border:1px solid #e4eaf2; background:#fff; color:#4a5e78; cursor:pointer; font-size:13px; }
.btn-icon:hover { background:#f4f7fb; }

.docs-list { display:flex; flex-direction:column; gap:12px; }
.doc-card  { background:#fff; border:1px solid #e4eaf2; border-radius:14px; overflow:hidden; }
.doc-header { display:flex; align-items:flex-start; gap:14px; padding:16px 20px; }
.doc-info  { flex:1; }
.doc-title-row { display:flex; align-items:center; gap:8px; flex-wrap:wrap; margin-bottom:5px; }
.doc-name  { font-size:15px; font-weight:700; color:#1a2b45; }
.doc-version-info { font-size:12px; color:#7a90b0; }
.doc-no-version   { color:#f59e0b; font-style:italic; }
.doc-icon-wrap    { display:inline-flex; align-items:center; justify-content:center; width:32px; height:32px; border-radius:8px; font-size:14px; flex-shrink:0; }
.doc-icon--nda    { background:#dbeafe; color:#1d4ed8; }
.doc-icon--induction { background:#ede9fe; color:#7c3aed; }
.doc-icon--policy { background:#dcfce7; color:#16a34a; }
.doc-icon--other  { background:#f1f5f9; color:#64748b; }
.doc-actions { display:flex; gap:8px; align-items:flex-start; flex-shrink:0; }

.type-badge--nda    { background:#dbeafe; color:#1d4ed8; }
.type-badge--induction { background:#ede9fe; color:#7c3aed; }
.type-badge--policy { background:#dcfce7; color:#16a34a; }
.type-badge--other  { background:#f1f5f9; color:#64748b; }
.badge { font-size:11px; font-weight:700; padding:2px 8px; border-radius:20px; }
.badge--green { background:#dcfce7; color:#16a34a; }
.badge--grey  { background:#f1f5f9; color:#64748b; }

.version-warning { background:#fffbeb; border:1px solid #fde68a; border-radius:8px; padding:10px 14px; font-size:12px; color:#92400e; display:flex; align-items:flex-start; gap:8px; margin-top:14px; }
.version-list { display:flex; flex-direction:column; gap:10px; }
.version-item { background:#f8fafb; border:1px solid #e4eaf2; border-radius:10px; padding:14px; }
.version-meta { display:flex; align-items:center; gap:10px; flex-wrap:wrap; margin-bottom:8px; }
.version-num  { font-size:13px; font-weight:700; color:#1a2b45; }
.version-effective, .version-created { font-size:12px; color:#7a90b0; }
.version-preview { font-size:12px; color:#4a5e78; line-height:1.5; font-family:monospace; background:#fff; border:1px solid #e4eaf2; border-radius:6px; padding:8px 10px; }

.empty-state { text-align:center; padding:60px 20px; }
.empty-icon  { font-size:48px; color:#dde5f0; margin-bottom:16px; }
.empty-title { font-size:16px; font-weight:700; color:#1a2b45; margin:0 0 8px; }
.empty-sub   { font-size:13px; color:#7a90b0; margin:0 0 20px; max-width:440px; margin-left:auto; margin-right:auto; }

.modal-backdrop { position:fixed; inset:0; background:rgba(10,20,40,.45); display:flex; align-items:center; justify-content:center; z-index:1000; padding:20px; }
.modal-box { background:#fff; border-radius:16px; width:100%; max-width:480px; max-height:90vh; overflow-y:auto; display:flex; flex-direction:column; box-shadow:0 20px 60px rgba(0,0,0,.2); }
.modal-box--sm { max-width:400px; }
.modal-header { display:flex; align-items:center; justify-content:space-between; padding:18px 22px 14px; border-bottom:1px solid #f0f4f8; }
.modal-title  { font-size:16px; font-weight:700; color:#1a2b45; margin:0; }
.modal-close  { background:none; border:none; font-size:16px; color:#7a90b0; cursor:pointer; padding:4px; }
.modal-body   { padding:22px; flex:1; overflow-y:auto; }
.modal-footer { padding:14px 22px; border-top:1px solid #f0f4f8; display:flex; justify-content:flex-end; gap:10px; }

.form-row  { display:flex; flex-direction:column; gap:5px; }
.form-label { font-size:12px; font-weight:600; color:#4a5e78; }
.form-input { width:100%; padding:9px 12px; border:1px solid #dde5f0; border-radius:8px; font-size:13px; color:#1a2b45; outline:none; box-sizing:border-box; font-family:inherit; }
.form-input:focus { border-color:#004AAD; }
.req { color:#e53e3e; }
.toggle-row  { display:flex; align-items:center; gap:10px; }
.toggle-name { font-size:13px; font-weight:600; color:#1a2b45; }
.toggle-switch { position:relative; display:inline-block; width:38px; height:22px; flex-shrink:0; }
.toggle-switch input { opacity:0; width:0; height:0; }
.toggle-track  { position:absolute; cursor:pointer; inset:0; background:#dde5f0; border-radius:22px; transition:.2s; }
.toggle-track::before { position:absolute; content:''; height:16px; width:16px; left:3px; top:3px; background:#fff; border-radius:50%; transition:.2s; }
.toggle-switch input:checked + .toggle-track { background:#004AAD; }
.toggle-switch input:checked + .toggle-track::before { transform:translateX(16px); }

.sk-table { background:#fff; border:1px solid #e4eaf2; border-radius:12px; overflow:hidden; }
.sk-row   { height:52px; background:linear-gradient(90deg,#f0f4f8 25%,#e4eaf2 50%,#f0f4f8 75%); background-size:200% 100%; animation:shimmer 1.4s infinite; border-bottom:1px solid #e4eaf2; }
@keyframes shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }
.form-hint { font-size:11px; color:#aab8cc; margin-top:2px; }

/* Quiz editor */
.quiz-editor-header { display:flex; align-items:flex-start; justify-content:space-between; flex-wrap:wrap; gap:10px; margin-bottom:12px; }
.quiz-empty { text-align:center; padding:16px; font-size:13px; color:#aab8cc; background:#f8fafc; border:1px dashed #dde5f0; border-radius:8px; margin-bottom:8px; }
.quiz-q-card { background:#f8fafc; border:1px solid #e4eaf2; border-radius:10px; padding:14px; margin-bottom:10px; }
.quiz-q-header { display:flex; align-items:center; gap:8px; margin-bottom:10px; }
.quiz-q-num { font-size:11px; font-weight:700; background:#ede9fe; color:#7c3aed; padding:3px 8px; border-radius:20px; flex-shrink:0; }
.quiz-opts { display:flex; flex-direction:column; gap:6px; padding-left:4px; }
.quiz-opt-row { display:flex; align-items:center; gap:8px; }
.quiz-opt-radio { display:flex; align-items:center; cursor:pointer; }
.quiz-opt-radio input[type="radio"] { width:16px; height:16px; accent-color:#7c3aed; cursor:pointer; }
</style>
